import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  ActivityTimeline,
  Badge,
  BackButton,
  Button,
  Card,
  CardHeader,
  ContentLoader,
  InsurerLogo,
  Modal,
  ModalFooter,
  Select,
} from '@/components/common';
import { PolicyNumber } from '@/components/common/PolicyNumber';
import { ServicingDocumentsCard, ServicingPaymentCard } from './ServicingWorkflowCards';
import {
  BROKER_ADMIN_ROLES,
  BROKER_SIDE_ROLES,
  readChecklist,
  readPayment,
  readWorkflowDocuments,
} from './serviceRequestWorkflow';
import type { ActivityItem } from '@/components/common';
import {
  useReopenServicingRequest,
  useServicingRequest,
  useUpdateServicingRequestStatus,
} from '@/hooks/useRenewals';
import { useMyPOSP } from '@/hooks/useBrokers';
import { useAuthStore } from '@/store/auth';
import { useCan } from '@/hooks/useEmployeeRights';
import { formatDateTime } from '@/utils';
import {
  REQUEST_STATUSES,
  activityTone,
  priorityBadgeVariant,
  priorityLabel,
  requestStatusBadgeVariant,
  requestTypeLabel,
  statusLabel,
} from './servicingRequestOptions';

// `BROKER_SIDE_ROLES` / `BROKER_ADMIN_ROLES` are imported from
// `serviceRequestWorkflow` rather than restated here: this page and the #223
// cards must agree on who is who, and two copies of the list would eventually
// not.
// #375: statuses a request cannot be moved out of except by an explicit Reopen.
const CLOSED_STATUSES = ['completed', 'rejected', 'cancelled'];
const POSP_ROLES = ['posp', 'super_posp'];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <div className="text-sm font-medium text-gray-900 dark:text-white">{children || '—'}</div>
    </div>
  );
}

export function ServicingRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const role = user?.role ?? '';

  const isBrokerSide = BROKER_SIDE_ROLES.includes(role);
  const isPosp = POSP_ROLES.includes(role);
  const isCustomer = role === 'customer';
  // B4: attaching paperwork, recording a payment and resolving are servicing
  // Edit on the API; `useCan` passes customers straight through.
  const canEditServicing = useCan('servicing', 'edit');

  // #223. Approving documents and verifying a payment are Broker-Admin-only,
  // enforced by `IsBrokerAdmin` on the server -- a broker employee and a POSP
  // holding `can_resolve_service_requests` both get a 403. Attaching paperwork
  // is wider (`IsBrokerEmployee | IsCustomer`), and recording a payment belongs
  // to the customer, with the broker side able to do it on their behalf.
  const canApproveWorkflow = BROKER_ADMIN_ROLES.includes(role);
  const canUploadDocuments = isCustomer || (BROKER_SIDE_ROLES.includes(role) && canEditServicing);
  const canRecordPayment = canUploadDocuments;

  const { data: request, isLoading, error } = useServicingRequest(id!);

  // The `can_resolve_service_requests` grant lives on the POSP record, not on
  // the login, so it has to be fetched. Only ever for a POSP -- `/posps/me/`
  // 404s for everyone else.
  const { data: myPosp } = useMyPOSP(isPosp);
  const canResolve =
    canEditServicing && (isBrokerSide || (isPosp && myPosp?.can_resolve_service_requests === true));

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [resolution, setResolution] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenReason, setReopenReason] = useState('');

  const updateStatus = useUpdateServicingRequestStatus();
  const reopen = useReopenServicingRequest();
  const queryClient = useQueryClient();

  // Every #223 write answers with the whole request, but the screen re-reads it
  // rather than trusting the write's own echo: the checklist, the payment record
  // and the activity history all move together, and one refetch keeps them
  // consistent instead of three states drifting apart.
  const refreshRequest = () => {
    queryClient.invalidateQueries({ queryKey: ['servicing-request', id] });
    queryClient.invalidateQueries({ queryKey: ['servicing-requests'] });
  };

  const activities: ActivityItem[] = useMemo(
    () =>
      (request?.activities ?? [])
        .slice()
        .sort((a, b) => new Date(b.activity_date).getTime() - new Date(a.activity_date).getTime())
        .map((activity) => ({
          id: activity.id,
          type: activityTone(activity.activity_type),
          title: activity.description,
          description:
            activity.old_status && activity.new_status
              ? `${statusLabel(activity.old_status)} → ${statusLabel(activity.new_status)}`
              : undefined,
          timestamp: activity.activity_date,
          user: activity.performed_by_name ? { name: activity.performed_by_name } : undefined,
        })),
    [request]
  );

  const closeStatusModal = () => {
    setShowStatusModal(false);
    setNewStatus('');
    setResolution('');
    setRejectionReason('');
  };

  const closeReopenModal = () => {
    setShowReopenModal(false);
    setReopenReason('');
  };

  const handleReopen = () => {
    if (!reopenReason.trim()) return;
    reopen.mutate({ id: id!, reason: reopenReason.trim() }, { onSuccess: closeReopenModal });
  };

  const statusSubmitDisabled =
    !newStatus ||
    (newStatus === 'completed' && !resolution.trim()) ||
    (newStatus === 'rejected' && !rejectionReason.trim());

  const handleStatusUpdate = () => {
    if (statusSubmitDisabled) return;
    updateStatus.mutate(
      {
        id: id!,
        status: newStatus,
        ...(newStatus === 'completed' ? { resolution } : {}),
        ...(newStatus === 'rejected' ? { rejection_reason: rejectionReason } : {}),
      },
      { onSuccess: closeStatusModal }
    );
  };

  if (isLoading) {
    return <ContentLoader />;
  }

  if (error || !request) {
    return (
      <div className="py-12 text-center">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Servicing request not available
        </h2>
        <p className="mt-2 text-gray-500 dark:text-gray-400">
          It either does not exist or is not one you have access to.
        </p>
        <Link to="/servicing-requests" className="mt-4 inline-block">
          <Button variant="outline" leftIcon={<ArrowLeftIcon className="h-5 w-5" />}>
            Back to Servicing Requests
          </Button>
        </Link>
      </div>
    );
  }

  const isClosed = CLOSED_STATUSES.includes(request.status);

  return (
    <div>
      <div className="mb-4">
        <BackButton to="/servicing-requests" />
      </div>

      <PageHeader
        title={request.request_number}
        subtitle={request.subject}
        breadcrumbs={[
          { label: 'Servicing Requests', href: '/servicing-requests' },
          { label: request.request_number },
        ]}
        actions={
          isClosed ? (
            canApproveWorkflow ? (
              <Button onClick={() => setShowReopenModal(true)}>Reopen</Button>
            ) : undefined
          ) : canResolve ? (
            <Button onClick={() => setShowStatusModal(true)}>Update Status</Button>
          ) : undefined
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader title="Request Details" />
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Field label="Request Type">{requestTypeLabel(request.request_type)}</Field>
              <Field label="Priority">
                <Badge variant={priorityBadgeVariant(request.priority)} size="sm">
                  {priorityLabel(request.priority)}
                </Badge>
              </Field>
              <Field label="Raised On">{formatDateTime(request.created_at)}</Field>
              <Field label="Last Updated">{formatDateTime(request.updated_at)}</Field>
            </div>
            <div className="mt-6 border-t border-gray-100 pt-6 dark:border-gray-700">
              <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">Description</p>
              <p className="whitespace-pre-wrap text-sm text-gray-900 dark:text-white">
                {request.description}
              </p>
            </div>
            {request.notes && (
              <div className="mt-6 border-t border-gray-100 pt-6 dark:border-gray-700">
                <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">Notes</p>
                <p className="whitespace-pre-wrap text-sm text-gray-900 dark:text-white">
                  {request.notes}
                </p>
              </div>
            )}
          </Card>

          <Card>
            <CardHeader title="Requester" />
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              <Field label="Name">{request.requester_name}</Field>
              <Field label="Email">
                {request.requester_email ? (
                  <a
                    href={`mailto:${request.requester_email}`}
                    className="text-primary-600 hover:underline dark:text-primary-400"
                  >
                    {request.requester_email}
                  </a>
                ) : null}
              </Field>
              <Field label="Phone">
                {request.requester_phone ? (
                  <a
                    href={`tel:${request.requester_phone}`}
                    className="text-primary-600 hover:underline dark:text-primary-400"
                  >
                    {request.requester_phone}
                  </a>
                ) : null}
              </Field>
            </div>
          </Card>

          {(request.resolution || request.rejection_reason) && (
            <Card>
              <CardHeader title={request.rejection_reason ? 'Rejection' : 'Resolution'} />
              <p className="whitespace-pre-wrap text-sm text-gray-900 dark:text-white">
                {request.rejection_reason || request.resolution}
              </p>
              {isClosed && request.resolved_at && (
                <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                  Closed {formatDateTime(request.resolved_at)}
                  {request.resolved_by_name ? ` by ${request.resolved_by_name}` : ''}
                </p>
              )}
            </Card>
          )}

          {/* #223 — Pending Documents. What replaced the read-only list here:
              the checklist of what this request type needs, upload, the Broker
              Admin's per-document approve/reject, and download through the
              authenticated route. The old list linked straight at `doc.file`,
              which is a `/policy/media/` URL nginx serves with no auth at all —
              a customer's ID proof behind nothing but the link. */}
          <ServicingDocumentsCard
            requestId={request.id}
            requestStatus={request.status}
            canApprove={canApproveWorkflow}
            canUpload={canUploadDocuments}
            onChanged={refreshRequest}
            documents={readWorkflowDocuments(request)}
            checklist={readChecklist(request)}
          />

          {/* #223 — Pending Payment. The customer records what they paid; only a
              Broker Admin verifies it, and only that verification moves the
              request to "With Insurer". */}
          <ServicingPaymentCard
            requestId={request.id}
            requestStatus={request.status}
            canApprove={canApproveWorkflow}
            canUpload={canUploadDocuments}
            canRecordPayment={canRecordPayment}
            onChanged={refreshRequest}
            payment={readPayment(request)}
          />

          <Card>
            <CardHeader title="Activity" />
            <ActivityTimeline activities={activities} />
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Status" />
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Current</span>
                <Badge variant={requestStatusBadgeVariant(request.status)}>
                  {statusLabel(request.status)}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Assigned To</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {request.assigned_to_name || 'Unassigned'}
                </span>
              </div>
              {request.sla_due_date && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">SLA Due</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatDateTime(request.sla_due_date)}
                  </span>
                </div>
              )}
              {request.is_sla_breached && (
                <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
                  <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-500" />
                  <span className="text-sm text-red-700 dark:text-red-300">SLA breached</span>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Policy" />
            <div className="space-y-4">
              <Field label="Policy Number">
                <PolicyNumber source={request} />
              </Field>
              <Field label="Customer">
                <span className="text-gray-900 dark:text-white">
                  {request.customer_name || '—'}
                </span>
              </Field>
              {/* U-05: the policy's insurer, with its logo and blurb. */}
              {request.insurer_name && (
                <Field label="Insurer">
                  <span className="flex items-center gap-2">
                    <InsurerLogo
                      src={request.insurer_logo_url}
                      name={request.insurer_name}
                      description={request.insurer_description}
                      size="xs"
                    />
                    <span className="min-w-0 truncate text-gray-900 dark:text-white">
                      {request.insurer_name}
                    </span>
                  </span>
                  {request.insurer_description && (
                    <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                      {request.insurer_description}
                    </span>
                  )}
                </Field>
              )}
              <Link to={`/policies/${request.policy}`} className="block">
                <Button variant="outline" className="w-full justify-start">
                  View Policy
                </Button>
              </Link>
            </div>
          </Card>

          {/* Who may act, spelled out rather than leaving a dead button. */}
          {isCustomer && (
            <Card>
              <div className="flex items-start gap-3">
                <ClockIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-400" />
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {request.status === 'pending_documents'
                    ? 'Your broker needs the documents listed above. Upload them here — a Broker Admin checks each one before the request moves on.'
                    : request.status === 'pending_payment'
                      ? 'Your broker is waiting on payment. Record it here once you have paid; a Broker Admin verifies the transaction before the request goes to the insurer.'
                      : 'Your broker is working on this request. Every update appears in the activity timeline — no action is needed from you unless they ask.'}
                </p>
              </div>
            </Card>
          )}
          {isPosp && !canResolve && (
            <Card>
              <div className="flex items-start gap-3">
                <ClockIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-400" />
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  You can follow this request because it is on a policy you sold. Your broker has
                  not granted you permission to resolve servicing requests.
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>

      <Modal isOpen={showReopenModal} onClose={closeReopenModal} title="Reopen Request">
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            This returns the request to In Progress and clears its resolution. The reason is
            recorded in the activity timeline.
          </p>
          <div>
            <label
              htmlFor="reopen-reason"
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              id="reopen-reason"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              rows={4}
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              placeholder="Why is this request being reopened?"
            />
          </div>
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={closeReopenModal}>
            Cancel
          </Button>
          <Button
            onClick={handleReopen}
            isLoading={reopen.isPending}
            disabled={!reopenReason.trim()}
          >
            Reopen Request
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={showStatusModal} onClose={closeStatusModal} title="Update Request Status">
        <div className="space-y-4">
          <Select
            name="new_status"
            label="New Status"
            options={[{ value: '', label: 'Select a status' }, ...REQUEST_STATUSES]}
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
          />
          {newStatus === 'completed' && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
                Resolution <span className="text-red-500">*</span>
              </label>
              <textarea
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                rows={4}
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                placeholder="What was done to resolve this request?"
              />
            </div>
          )}
          {newStatus === 'rejected' && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                rows={4}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Why is this request being rejected?"
              />
            </div>
          )}
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={closeStatusModal}>
            Cancel
          </Button>
          <Button
            onClick={handleStatusUpdate}
            isLoading={updateStatus.isPending}
            disabled={statusSubmitDisabled}
          >
            Update Status
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
